import { useState } from "react";
import { motion } from "framer-motion";
import {
  Zap,
  ShieldCheck,
  Scale,
  BrainCircuit,
  Languages,
  Megaphone,
  NotebookPen,
  Check,
} from "lucide-react";

const languagePreviews = [
  { code: "EN", label: "English", line: "Markets rally as the central bank holds rates steady." },
  { code: "हिं", label: "हिन्दी", line: "ब्याज दरें स्थिर रखते ही बाज़ार में तेज़ी आ गई।" },
  { code: "த", label: "தமிழ்", line: "வட்டி விகிதம் மாறாததால் சந்தைகள் உயர்ந்தன." },
  { code: "বাং", label: "বাংলা", line: "সুদের হার অপরিবর্তিত থাকায় বাজারে জোরালো উত্থান।" },
  { code: "తె", label: "తెలుగు", line: "వడ్డీ రేట్లు స్థిరంగా ఉండటంతో మార్కెట్లు పుంజుకున్నాయి." },
  { code: "मरा", label: "मराठी", line: "व्याजदर स्थिर ठेवल्याने शेअर बाजारात तेजी आली." },
];

const Card = ({ icon: Icon, tag, title, desc, span, testid, children }) => (
  <motion.div
    data-testid={testid}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.55 }}
    className={`glow-card rounded-2xl border border-[#1E293B] bg-gradient-to-b from-slate-800/60 to-slate-900/80 p-6 sm:p-8 ${span}`}
  >
    <div className="flex items-center gap-3">
      <span className="h-10 w-10 rounded-lg border border-cyan-500/25 bg-cyan-500/10 flex items-center justify-center">
        <Icon className="h-5 w-5 text-cyan-400" />
      </span>
      <span className="mono-label !text-[10px]">{tag}</span>
    </div>
    <h3 className="font-display text-lg sm:text-xl font-semibold mt-4">{title}</h3>
    <p className="text-sm text-slate-400 leading-relaxed mt-2">{desc}</p>
    {children}
  </motion.div>
);

const Features = () => {
  const [lang, setLang] = useState(0);

  return (
    <section id="features" data-testid="features-section" className="py-20 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="mono-label">The Feature Stack</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-4">
            Seven tools. One honest feed.
          </h2>
          <p className="text-slate-400 mt-4 leading-relaxed">
            Every feature in InNewZ exists for one reason — to make you the most informed
            person in the room, in the least amount of time.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-12 gap-5">
          <Card
            icon={Zap}
            tag="Short News"
            title="The whole story in 60 words"
            desc="No 2,000-word essays for a two-line update. AI distills every story into a crisp, complete brief you finish before your chai cools — with full articles one tap away when you want depth."
            span="col-span-12 md:col-span-7"
            testid="feature-card-short-news"
          >
            <div className="mt-5 flex items-center gap-2 text-xs font-mono-x text-slate-500">
              <Check className="h-3.5 w-3.5 text-emerald-400" /> Swipe-through briefs
              <Check className="h-3.5 w-3.5 text-emerald-400 ml-4" /> 18-second reads
            </div>
          </Card>

          <Card
            icon={ShieldCheck}
            tag="Fact Checking"
            title="Truth meter on every story"
            desc="Each brief carries a live credibility score built from source authenticity, cross-references, media forensics, and expert consensus."
            span="col-span-12 md:col-span-5"
            testid="feature-card-fact-check"
          >
            <div className="mt-5">
              <div className="flex justify-between text-[10px] font-mono-x uppercase tracking-widest text-slate-500">
                <span>Sample score</span>
                <span className="text-emerald-400">Verified</span>
              </div>
              <div className="h-1.5 rounded-full bg-slate-800 mt-2 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: "94%" }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.2, ease: "easeOut" }}
                  className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-cyan-400"
                />
              </div>
            </div>
          </Card>

          <Card
            icon={Scale}
            tag="Source Radar"
            title="Left, center, right — side by side"
            desc="See how every outlet frames the same story. Spot the spin before it spots you."
            span="col-span-12 md:col-span-4"
            testid="feature-card-source-radar"
          />
          <Card
            icon={BrainCircuit}
            tag="AI Verification"
            title="Bias & hallucination shield"
            desc="Our AI flags loaded language, fake media, and unsupported claims before they reach your feed."
            span="col-span-12 md:col-span-4"
            testid="feature-card-ai-verification"
          />
          <Card
            icon={Languages}
            tag="Regional Languages"
            title="News that speaks your language"
            desc="Instant briefs in Hindi, Tamil, Bengali, Telugu, Marathi and more — written natively, not translated."
            span="col-span-12 md:col-span-4"
            testid="feature-card-languages"
          >
            <div className="mt-5 flex flex-wrap gap-1.5">
              {languagePreviews.map((l, i) => (
                <button
                  key={l.code}
                  data-testid={`language-chip-${i}`}
                  onClick={() => setLang(i)}
                  className={`rounded-full border px-2.5 py-1 text-[11px] font-mono-x transition-colors duration-200 ${
                    lang === i
                      ? "border-cyan-400 bg-cyan-500/15 text-cyan-300"
                      : "border-slate-700 text-slate-500 hover:text-slate-300"
                  }`}
                >
                  {l.code}
                </button>
              ))}
            </div>
            <p data-testid="language-preview" className="mt-3 text-sm text-slate-300 border-l-2 border-cyan-500/50 pl-3">
              {languagePreviews[lang].line}
              <span className="block mt-1 text-[10px] font-mono-x uppercase tracking-widest text-slate-500">
                {languagePreviews[lang].label}
              </span>
            </p>
          </Card>

          <Card
            icon={Megaphone}
            tag="Public Reporting"
            title="Your street is the newsroom"
            desc="Witness something? Post it. Community reports go through AI screening and editor verification before publishing — citizen journalism with guardrails."
            span="col-span-12 md:col-span-5"
            testid="feature-card-public-reporting"
          />
          <Card
            icon={NotebookPen}
            tag="Student Notebook"
            title="News that preps you for exams"
            desc="Every brief auto-tagged to UPSC, SSC, Banking and GRE syllabi. Save to your notebook, get key takeaways, and revise with auto-generated flashcards."
            span="col-span-12 md:col-span-7"
            testid="feature-card-student-notebook"
          >
            <div className="mt-5 flex flex-wrap gap-2">
              {["UPSC GS-2", "SSC CGL", "Banking", "GRE"].map((chip) => (
                <span key={chip} className="rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-[10px] font-mono-x uppercase tracking-widest text-amber-300">
                  {chip}
                </span>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Features;
